/* ═════════════════════════ PendingRequestsCard ═════════════════════
   Settings-page card for admins. Lists pending join requests
   (GET /api/join-requests) and lets the admin pick a role + approve
   or reject each one (POST /api/join-requests/:id/approve|reject).

   Polls every 30s. Admin-only — don't render for non-admin users.
   ═══════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useState } from "react";
import { UserPlus, Check, X, Shield } from "lucide-react";
import { ROLE_LABELS } from "./useAuth.js";

const INK = "#000000";
const MUTED = "#6B7280";
const BORDER = "#E4E4E7";
const SOFT = "#F4F4F5";

export default function PendingRequestsCard() {
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  const [rolePicks, setRolePicks] = useState({}); // { requestId: role }

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/join-requests", { credentials: "same-origin" });
      if (!r.ok) { setRequests([]); return; }
      const data = await r.json();
      setRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch { setRequests([]); }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function approve(id) {
    const role = rolePicks[id] || "roaster";
    setBusyId(id); setErr("");
    try {
      const r = await fetch(`/api/join-requests/${encodeURIComponent(id)}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ role }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || "Couldn't approve");
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusyId(null); }
  }

  async function reject(id) {
    setBusyId(id); setErr("");
    try {
      const r = await fetch(`/api/join-requests/${encodeURIComponent(id)}/reject`, {
        method: "POST", credentials: "same-origin",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || "Couldn't reject");
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusyId(null); }
  }

  // Don't render at all when there's nothing to show — keeps the settings
  // page visually clean for admins during quiet periods.
  if (loaded && requests.length === 0) return null;

  return (
    <div style={{
      background: "#fff", borderRadius: 12, border: "1px solid #F3F4F6",
      padding: 24, marginBottom: 16, fontFamily: "Inter, sans-serif",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, background: "#EEF2FF",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <UserPlus size={20} color="#4338CA" strokeWidth={1.6} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: INK }}>Pending join requests</div>
          <div style={{ fontSize: 15, color: MUTED, marginTop: 1 }}>
            People who signed in with Google and are waiting to be added.
          </div>
        </div>
        {requests.length > 0 && (
          <span style={{
            display: "inline-flex", alignItems: "center", padding: "3px 10px",
            borderRadius: 99, background: "#EEF2FF", color: "#4338CA",
            fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
          }}>{requests.length}</span>
        )}
      </div>

      {err && (
        <div style={{
          marginBottom: 12, padding: "10px 14px", borderRadius: 8,
          background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
          fontSize: 13, lineHeight: 1.5,
        }}>{err}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {requests.map(req => {
          const role = rolePicks[req.id] || "roaster";
          const busy = busyId === req.id;
          return (
            <div key={req.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px", background: SOFT, borderRadius: 10,
              border: `1px solid ${BORDER}`,
            }}>
              <div style={{
                flexShrink: 0, width: 36, height: 36, borderRadius: "50%",
                background: "#fff", border: `1px solid ${BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 600, color: INK,
              }}>{initials(req)}</div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: INK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {req.name || req.email}
                </div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {req.email} · {timeAgo(req.createdAt)}
                </div>
              </div>

              <select
                value={role}
                onChange={e => setRolePicks(p => ({ ...p, [req.id]: e.target.value }))}
                disabled={busy}
                style={{
                  padding: "7px 10px", borderRadius: 8, border: `1px solid ${BORDER}`,
                  background: "#fff", fontSize: 13, color: INK,
                  fontFamily: "Inter, sans-serif", cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>

              <button
                onClick={() => approve(req.id)}
                disabled={busy}
                title={`Approve as ${ROLE_LABELS[role] || role}`}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "7px 12px", borderRadius: 8,
                  background: busy ? "#9CA3AF" : INK,
                  border: `1px solid ${busy ? "#9CA3AF" : INK}`,
                  color: "#fff", fontSize: 13, fontWeight: 600,
                  cursor: busy ? "not-allowed" : "pointer",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                <Check size={13} strokeWidth={2.2} />
                Approve
              </button>
              <button
                onClick={() => reject(req.id)}
                disabled={busy}
                title="Reject this request"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "7px 10px", borderRadius: 8,
                  background: "#fff", border: `1px solid ${BORDER}`,
                  color: MUTED, fontSize: 13, fontWeight: 500,
                  cursor: busy ? "not-allowed" : "pointer",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                <X size={13} strokeWidth={2} />
                Reject
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, color: MUTED, fontSize: 12 }}>
        <Shield size={12} strokeWidth={1.6} />
        <span>Approving adds the person to your team. Rejecting wipes their pending session.</span>
      </div>
    </div>
  );
}

function initials(req) {
  const src = req.name || req.email || "?";
  const parts = src.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.split("@")[0].slice(0, 2).toUpperCase();
}

function timeAgo(ts) {
  if (!ts) return "";
  const d = Math.floor((Date.now() - ts) / 1000);
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}
